import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 38. API-FIRST
// Zifek Core expose des APIs internes et externes structurées.
// REST aujourd'hui (GraphQL plus tard), webhooks et events.
// Chaque API utilise : authentication · authorization · validation ·
// rate limiting · logging.
// Ce socle gère les clés d'API, les webhooks et documente les endpoints.
// ─────────────────────────────────────────────────────────────

/** Décrit un endpoint REST exposé par le socle (documentation vivante). */
export interface ApiEndpointDoc {
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  path: string;
  label: string;
  desc: string;
  requiresAuth: boolean;
}

export const API_ENDPOINTS: ApiEndpointDoc[] = [
  { method: 'GET', path: '/functions/v1/core-api/ping', label: 'Santé de l’API', desc: 'Vérifie la clé et renvoie les scopes + le rate limit.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/customers', label: 'Lister les clients (API)', desc: 'Clients de l’organisation via clé d’API — scope read.', requiresAuth: true },
  { method: 'POST', path: '/functions/v1/core-api/customers', label: 'Créer un client (API)', desc: 'Crée une fiche client via clé d’API — scope write.', requiresAuth: true },
  { method: 'POST', path: '/functions/v1/core-api/events', label: 'Émettre un événement (API)', desc: 'Publie un événement via clé d’API — scope events.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/timeline', label: 'Lire la Timeline (API)', desc: 'Interactions d’une entité via clé d’API — scope read.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/ai/tools', label: 'Lister les outils IA (API)', desc: 'Catalogue des services exposés au Zifek AI Tool Gateway — scope read.', requiresAuth: true },
  { method: 'POST', path: '/functions/v1/core-api/ai/invoke', label: 'Invoquer un outil IA (API)', desc: 'Exécute search_customers, get_customer_orders… avec authorization + validation — scope read. Écritures du Core (create_appointment, create_booking, create_invoice) exécutées via l’Action Layer serveur (idempotence + journal) — scope write.', requiresAuth: true },
  { method: 'POST', path: '/functions/v1/core-api/ai/chain', label: 'Enchaîner des outils IA (API)', desc: 'Function-calling multi-étapes : exécute une suite d’outils en réutilisant les résultats ({{alias.chemin}}) — scopes selon l’outil.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/actions', label: 'Journal des actions (API)', desc: 'Liste les actions du socle (action_id, actor, resource, status) — scope read.', requiresAuth: true },
  { method: 'POST', path: '/functions/v1/core-api/actions', label: 'Exécuter une action (API)', desc: 'Action Layer : permission → idempotence → exécution → journalisation. Actions réelles : create_customer, reserve_resource (contrôle de conflit serveur) — scope write.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/idempotency', label: 'Clés d’idempotence (API)', desc: 'Liste les clés mémorisées pour éviter les doublons — scope read.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/storage', label: 'Fournisseurs de stockage (API)', desc: 'Liste les fournisseurs de stockage (SeaweedFS, S3, cloud) — scope read.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/settings', label: 'Paramètres résolus (API)', desc: 'Paramètres effectifs après cascade Platform → Country → Business → Branch → User — scope read.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/profile', label: 'Profil public (API)', desc: 'Profil standardisé de l’entreprise (Zifek Space, SEO, AI) — scope read.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/verifications', label: 'Demandes de vérification (API)', desc: 'Niveaux Basic / Business / Professional — scope read.', requiresAuth: true },
  { method: 'POST', path: '/functions/v1/core-api/verifications', label: 'Demander une vérification (API)', desc: 'Dépose une demande de vérification au niveau visé — scope write.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/reviews', label: 'Lister les avis (API)', desc: 'Avis de l’organisation (avis, note, statut) — scope read.', requiresAuth: true },
  { method: 'POST', path: '/functions/v1/core-api/reviews', label: 'Créer un avis (API)', desc: 'Dépose un avis (status pending, modération) — scope write.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/favorites', label: 'Lister les favoris (API)', desc: 'Favoris d’un utilisateur (user_id) — scope read.', requiresAuth: true },
  { method: 'POST', path: '/functions/v1/core-api/favorites', label: 'Ajouter un favori (API)', desc: 'Sauvegarde idempotente d’une entité — scope write.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/consents', label: 'Lister les consentements (API)', desc: 'Consentements d’un utilisateur (user_id) — scope read.', requiresAuth: true },
  { method: 'POST', path: '/functions/v1/core-api/consents', label: 'Définir un consentement (API)', desc: 'Accorde ou retire un consentement (journalisé) — scope write.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/privacy', label: 'Demandes de confidentialité (API)', desc: 'Demandes d’accès / export / rectification / suppression — scope read.', requiresAuth: true },
  { method: 'POST', path: '/functions/v1/core-api/privacy', label: 'Créer une demande RGPD (API)', desc: 'Dépose une demande de confidentialité — scope write.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/modules', label: 'Registre des modules (API)', desc: 'Modules fusionnés (plateforme + entreprise) — scope read.', requiresAuth: true },
  { method: 'POST', path: '/functions/v1/core-api/modules', label: 'Activer un module (API)', desc: 'Active ou désactive un module — scope write.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/flags', label: 'Feature flags (API)', desc: 'Flags et pourcentage de déploiement — scope read.', requiresAuth: true },
  { method: 'POST', path: '/functions/v1/core-api/flags', label: 'Configurer un flag (API)', desc: 'Active un flag pour l’entreprise — scope write.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/versions', label: 'Versions de l’API (API)', desc: 'Versions publiées (/api/v1/…, /api/v2/…) — scope read.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/rate-limits', label: 'Politiques de limitation (API)', desc: 'Rate limiting fusionné (API, recherche, auth, AI Tools, actions, webhooks) — scope read.', requiresAuth: true },
  { method: 'POST', path: '/functions/v1/core-api/rate-limits', label: 'Définir une limite (API)', desc: 'Crée ou met à jour une politique de rate limiting — scope write.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/cache', label: 'Entrées de cache (API)', desc: 'Cache tenant-aware (scopes cachables uniquement) — scope read.', requiresAuth: true },
  { method: 'POST', path: '/functions/v1/core-api/cache', label: 'Mettre en cache (API)', desc: 'Écrit une entrée en cache ; refuse disponibilité temps réel, permissions et données sensibles — scope write.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/performance', label: 'Cibles de performance (API)', desc: 'Objectifs de montée en charge progressive — scope read.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/observability', label: 'Observabilité (API)', desc: 'Métriques et traces (latence, erreurs, usage IA, isolation) — scope read.', requiresAuth: true },
  { method: 'POST', path: '/functions/v1/core-api/observability', label: 'Enregistrer une métrique (API)', desc: 'Publie une métrique d’observabilité — scope write.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/backups', label: 'Sauvegardes (API)', desc: 'Historique des sauvegardes et points de restauration — scope read.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/integrity', label: 'Intégrité des données (API)', desc: 'Contrôles (foreign keys, unicité, validations, indexes) — scope read.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/transactions', label: 'Transactions (API)', desc: 'Journal des opérations multi-étapes (commit / rollback) — scope read.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/migrations', label: 'Migrations (API)', desc: 'Schéma versionné (version, rollback, statut) — scope read.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/seed', label: 'Données par défaut (API)', desc: 'Seed du socle : pays, devises, langues, catégories, rôles, permissions, statuts — scope read.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/schema', label: 'Architecture des tables (API)', desc: 'Entités centrales, tables de modules et relations — scope read.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/boundaries', label: 'Frontières Core / modules (API)', desc: 'Capacités du Core vs domaines réservés aux modules — scope read.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/scenarios', label: 'Scénarios entrepreneur / client (API)', desc: 'Composition Core + modules et parcours client IA — scope read.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/ai/security', label: 'Sécurité IA (API)', desc: 'Politiques de la passerelle et décisions (allow/deny) — scope read.', requiresAuth: true },
  { method: 'GET', path: '/functions/v1/core-api/foundation', label: 'Fondation permanente (API)', desc: 'Systèmes fondamentaux réutilisables et leur état réel — scope read.', requiresAuth: true },
  { method: 'GET', path: '/rest/v1/core_customers', label: 'Lister les clients', desc: 'Renvoie les clients de l’organisation.', requiresAuth: true },
  { method: 'POST', path: '/rest/v1/core_customers', label: 'Créer un client', desc: 'Crée une fiche client.', requiresAuth: true },
  { method: 'GET', path: '/rest/v1/core_calendar_events', label: 'Lister les événements', desc: 'Agenda central de l’organisation.', requiresAuth: true },
  { method: 'POST', path: '/rest/v1/core_conversations', label: 'Ouvrir une conversation', desc: 'Crée une conversation liée à une entité.', requiresAuth: true },
  { method: 'POST', path: '/functions/v1/send-booking-notification', label: 'Notifier une réservation', desc: 'Déclenche l’envoi de la notification de réservation.', requiresAuth: true },
  { method: 'POST', path: '/rest/v1/core_event_log', label: 'Émettre un événement', desc: 'Publie un événement dans l’Event Bus.', requiresAuth: true },
];

const METHOD_CLASSES: Record<string, string> = {
  GET: 'bg-secondary-100 text-secondary-900',
  POST: 'bg-primary-100 text-primary-900',
  PATCH: 'bg-accent-100 text-accent-900',
  DELETE: 'bg-foreground-100 text-foreground-700',
};

export function methodClass(method: string): string {
  return METHOD_CLASSES[method] || METHOD_CLASSES.GET;
}

export interface CoreApiKey {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  name: string;
  key_prefix: string | null;
  key_hash: string | null;
  scopes: unknown;
  rate_limit_per_min: number | null;
  status: string;
  last_used_at: string | null;
  expires_at: string | null;
  created_at: string | null;
}

export const API_SCOPES: { key: string; label: string }[] = [
  { key: 'read', label: 'Lecture' },
  { key: 'write', label: 'Écriture' },
  { key: 'events', label: 'Événements' },
  { key: 'webhooks', label: 'Webhooks' },
];

/** Hachage léger (non cryptographique) — la clé complète n'est montrée qu'une fois. */
function lightHash(value: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < value.length; i += 1) {
    const ch = value.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (h2 >>> 0).toString(16).padStart(8, '0') + (h1 >>> 0).toString(16).padStart(8, '0');
}

function randomToken(len = 32): string {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let out = '';
  const cryptoObj = typeof crypto !== 'undefined' ? crypto : null;
  if (cryptoObj?.getRandomValues) {
    const arr = new Uint8Array(len);
    cryptoObj.getRandomValues(arr);
    for (let i = 0; i < len; i += 1) out += chars[arr[i] % chars.length];
  } else {
    for (let i = 0; i < len; i += 1) out += chars[Math.floor(Math.random() * chars.length)];
  }
  return out;
}

export async function listApiKeys(organizationId: number): Promise<CoreApiKey[]> {
  const { data, error } = await supabase
    .from('core_api_keys')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as CoreApiKey[];
}

export interface NewApiKey {
  id: number;
  fullKey: string;
}

/** Crée une clé d'API et renvoie la clé complète (affichée une seule fois). */
export async function createApiKey(input: {
  tenantId: number | null;
  organizationId: number;
  name: string;
  scopes: string[];
  rateLimit?: number;
  createdBy?: number | null;
}): Promise<NewApiKey | null> {
  const secret = randomToken(36);
  const fullKey = `zfk_${secret}`;
  const prefix = fullKey.slice(0, 12);
  const { data, error } = await supabase
    .from('core_api_keys')
    .insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      name: input.name,
      key_prefix: prefix,
      key_hash: lightHash(fullKey),
      scopes: input.scopes,
      rate_limit_per_min: input.rateLimit ?? 60,
      status: 'active',
      created_by: input.createdBy ?? null,
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  if (!data?.id) return null;
  return { id: data.id as number, fullKey };
}

export async function updateApiKey(
  id: number,
  patch: { name?: string; scopes?: string[]; rateLimit?: number; status?: string },
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (patch.name !== undefined) payload.name = patch.name;
  if (patch.scopes !== undefined) payload.scopes = patch.scopes;
  if (patch.rateLimit !== undefined) payload.rate_limit_per_min = patch.rateLimit;
  if (patch.status !== undefined) payload.status = patch.status;
  if (Object.keys(payload).length === 0) return;
  const { error } = await supabase.from('core_api_keys').update(payload).eq('id', id);
  if (error) throw error;
}

export async function revokeApiKey(id: number): Promise<void> {
  const { error } = await supabase.from('core_api_keys').update({ status: 'revoked' }).eq('id', id);
  if (error) throw error;
}

export async function deleteApiKey(id: number): Promise<void> {
  const { error } = await supabase.from('core_api_keys').delete().eq('id', id);
  if (error) throw error;
}

// ── Webhooks ──

export interface CoreWebhook {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  name: string;
  url: string;
  events: string[] | null;
  secret: string | null;
  status: string;
  last_status: number | null;
  last_called_at: string | null;
  created_at: string | null;
}

export async function listWebhooks(organizationId: number): Promise<CoreWebhook[]> {
  const { data, error } = await supabase
    .from('core_webhooks')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as CoreWebhook[];
}

export async function createWebhook(input: {
  tenantId: number | null;
  organizationId: number;
  name: string;
  url: string;
  events: string[];
}): Promise<number | null> {
  const { data, error } = await supabase
    .from('core_webhooks')
    .insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      name: input.name,
      url: input.url,
      events: input.events,
      secret: `whsec_${randomToken(24)}`,
      status: 'active',
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function updateWebhook(
  id: number,
  patch: { name?: string; url?: string; events?: string[]; status?: string },
): Promise<void> {
  const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.name !== undefined) payload.name = patch.name;
  if (patch.url !== undefined) payload.url = patch.url;
  if (patch.events !== undefined) payload.events = patch.events;
  if (patch.status !== undefined) payload.status = patch.status;
  const { error } = await supabase.from('core_webhooks').update(payload).eq('id', id);
  if (error) throw error;
}

export async function deleteWebhook(id: number): Promise<void> {
  const { error } = await supabase.from('core_webhooks').delete().eq('id', id);
  if (error) throw error;
}